import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { fetchFeatureFlags, updateFeatureFlag } from "./api";
import PollLoanFilterCard from "./PollLoanFilterCard";
import { StateBlock } from "./shared";

/**
 * Section headings for the groups the backend registry hands back. An unknown
 * group still renders — under its own id — rather than vanishing, so a flag
 * added to the backend is never invisible here.
 */
const GROUP_LABELS = {
  "newfi-split-edocs": "Newfi split eDocs",
  meridianlink: "MeridianLink",
  "newfi-automations": "Newfi automations",
};

const GROUP_ORDER = ["newfi-split-edocs", "meridianlink", "newfi-automations"];

const GROUP_NOTES = {
  "newfi-split-edocs":
    "Four independent switches, all off unless somebody turned them on. " +
    "They are separate on purpose: the pipeline can run over hand-seeded " +
    "records with the poll paused, so nothing new is pulled out of a real " +
    "customer's loan while the feature is being exercised. The poll flag " +
    "answers whether detection runs at all; the loan filter above answers " +
    "which loans it may touch, and the two are set independently.",
};

/**
 * One flag.
 *
 * The row states what is true right now — "running" or "not running" — instead
 * of the stored boolean. Half these flags are kill switches, where `true` means
 * the feature is OFF, and a switch whose meaning flips between adjacent rows is
 * how the wrong one gets flipped at 2am.
 */
function FlagRow({ flag, env, busy, onToggle }) {
  const active = flag.isFeatureActive;
  const nextValue = !flag.value;

  return (
    <div className={`nse-flag ${active ? "is-on" : "is-off"}`}>
      <div className="nse-flag__main">
        <div className="nse-flag__head">
          <span className="nse-flag__label">{flag.label}</span>
          <span
            className={`nse-pill ${active ? "nse-pill--ready" : "nse-pill--out_of_scope"}`}
          >
            {active ? "Running" : "Not running"}
          </span>
          {!flag.isSetInDatabase && (
            <span
              className="nse-tag"
              title={
                "No field for this flag exists on whereis/feature-flags in " +
                `${env}. Readers are falling back to the default (${String(flag.defaultValue)}).`
              }
            >
              default
            </span>
          )}
        </div>
        <p className="nse-flag__desc">{flag.description}</p>
        <div className="nse-flag__meta">
          <span className="nse-mono" title="Firestore field on whereis/feature-flags">
            {flag.name}
          </span>
          <span className="nse-muted">
            stored: {flag.isSetInDatabase ? String(flag.value) : "—"} · default:{" "}
            {String(flag.defaultValue)} ·{" "}
            {flag.polarity === "enable"
              ? "true turns the feature on"
              : "true turns the feature off"}
          </span>
        </div>
      </div>

      <button
        type="button"
        className={`nse-btn ${active ? "" : "nse-btn--primary"}`}
        disabled={busy || !flag.toggleable}
        onClick={() => onToggle(flag, nextValue)}
        title={
          flag.toggleable
            ? `Set ${flag.name} to ${String(nextValue)} in ${env}`
            : "This flag is not toggleable from the dashboard"
        }
      >
        {busy ? "Saving…" : active ? "Turn off" : "Turn on"}
      </button>
    </div>
  );
}

export default function FeatureFlagsTab({ env }) {
  const [flags, setFlags] = useState([]);
  const [documentExists, setDocumentExists] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyFlag, setBusyFlag] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchFeatureFlags(env)
      .then((res) => {
        if (res?.success) {
          setFlags(res.flags || []);
          setDocumentExists(res.documentExists !== false);
        } else {
          setError(res?.reason || "The backend did not return any flags.");
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [env]);

  useEffect(load, [load]);

  const onToggle = async (flag, value) => {
    // A flag that is off because nobody ever set it and one that is off because
    // somebody set it read the same in the UI, so the confirmation names the
    // environment and the resulting behaviour, not the boolean.
    const willRun =
      flag.polarity === "enable" ? value === true : value !== true;
    const confirmed = window.confirm(
      `${env.toUpperCase()}: set ${flag.name} to ${String(value)}?\n\n` +
        `${flag.label} will be ${willRun ? "RUNNING" : "NOT running"} after this.`,
    );
    if (!confirmed) {
      return;
    }

    setBusyFlag(flag.name);
    try {
      const res = await updateFeatureFlag(
        { flagName: flag.name, value },
        env,
      );
      if (!res?.success) {
        throw new Error(res?.reason || "The backend rejected the change.");
      }
      // Render what the backend stored, not what we asked for.
      setFlags((current) =>
        current.map((entry) =>
          entry.name === flag.name ? { ...entry, ...res.flag } : entry,
        ),
      );
      setDocumentExists(true);
      toast.success(
        `${flag.label} is now ${willRun ? "running" : "off"} in ${env}.`,
      );
    } catch (err) {
      toast.error(`Could not update ${flag.name}: ${err.message}`);
    } finally {
      setBusyFlag(null);
    }
  };

  const grouped = useMemo(() => {
    const buckets = new Map();
    flags.forEach((flag) => {
      const key = flag.group || "other";
      if (!buckets.has(key)) {
        buckets.set(key, []);
      }
      buckets.get(key).push(flag);
    });
    return [...buckets.entries()].sort(
      (a, b) =>
        (GROUP_ORDER.indexOf(a[0]) + 1 || 99) -
        (GROUP_ORDER.indexOf(b[0]) + 1 || 99),
    );
  }, [flags]);

  return (
    <>
      <div className="nse-flags-head">
        <p className="nse-subtitle">
          Live values from <code>whereis/feature-flags</code> in{" "}
          <strong>{env}</strong>. A change takes effect on the next read — the
          crons check on every tick, so within about five minutes.
        </p>
        <button type="button" className="nse-btn" onClick={load} disabled={loading}>
          Reload
        </button>
      </div>

      {!documentExists && !loading && !error && (
        <div className="nse-notice nse-notice--warn">
          <strong>No flag document in {env} yet.</strong> Every flag below is
          showing its default. The document is created the first time you toggle
          anything.
        </div>
      )}

      {/* Above the flag list and outside its StateBlock: the filter is what
          decides how much of production the poll flag exposes, so it must
          still render — and still be fixable — when the flag registry itself
          fails to load. */}
      <PollLoanFilterCard env={env} />

      <StateBlock
        loading={loading}
        error={error}
        empty={!loading && !error && flags.length === 0}
        emptyText="The backend registry is empty."
      >
        <div className="nse-flag-groups">
          {grouped.map(([group, entries]) => (
            <section key={group} className="nse-card">
              <div className="nse-card__head">
                <h2 className="nse-card__title">
                  {GROUP_LABELS[group] || group}
                </h2>
              </div>
              {GROUP_NOTES[group] && (
                <p className="nse-flag-group__note">{GROUP_NOTES[group]}</p>
              )}
              <div className="nse-flag-list">
                {entries.map((flag) => (
                  <FlagRow
                    key={flag.name}
                    flag={flag}
                    env={env}
                    busy={busyFlag === flag.name}
                    onToggle={onToggle}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </StateBlock>
    </>
  );
}
